import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal, Spin, Alert, Button } from 'antd';
import { ExclamationCircleOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useUser } from '../UserContext';
import { esAdministrador } from '../utils/perfil';

/**
 * Guard que verifica si el conductor tiene firma registrada
 * Si no la tiene, muestra un modal y redirige a la página de firma
 * Si no tiene vehículo, muestra modo visualizador
 */
const FirmaGuardConductor = ({ children }) => {
  const { tieneFirma, verificarFirma } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [verificando, setVerificando] = useState(true);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [mostrarModalVisualizador, setMostrarModalVisualizador] = useState(false);
  const [modalVisualizadorMostrado, setModalVisualizadorMostrado] = useState(false);

  // Verificar si tiene vehículo asignado
  const vehiculoPrincipal = localStorage.getItem("vehiculoPrincipal");
  const tieneVehiculo = vehiculoPrincipal && vehiculoPrincipal !== "null" && vehiculoPrincipal !== "undefined";

  useEffect(() => {
    const checkFirma = async () => {
      console.log('=== FIRMA GUARD - CHECK ===');
      console.log('Location:', location.pathname);
      console.log('TieneFirma (estado actual):', tieneFirma);
      console.log('TieneVehiculo:', tieneVehiculo);

      // No verificar si ya estamos en la página de firma
      if (location.pathname.includes('/Firma')) {
        console.log('En página de firma, no verificar');
        setVerificando(false);
        setMostrarModal(false);
        return;
      }

      // Si NO tiene vehículo, mostrar modal de visualizador (solo una vez por sesión)
      if (!tieneVehiculo && !modalVisualizadorMostrado) {
        console.log('Sin vehículo - modo visualizador');
        setVerificando(false);
        setMostrarModalVisualizador(true);
        setModalVisualizadorMostrado(true);
        return;
      }

      // Si no tiene vehículo y ya se mostró el modal, no verificar firma
      if (!tieneVehiculo) {
        setVerificando(false);
        setMostrarModal(false);
        return;
      }

      // Siempre verificar la firma si es null, o re-verificar cuando cambie
      if (tieneFirma === null) {
        console.log('Verificando firma por primera vez...');
        const tiene = await verificarFirma();
        console.log('Resultado verificación:', tiene);
        setVerificando(false);
        setMostrarModal(!tiene);
      } else {
        console.log('Estado de firma conocido:', tieneFirma);
        setVerificando(false);
        setMostrarModal(!tieneFirma);
      }
    };

    checkFirma();
  }, [tieneFirma, verificarFirma, location.pathname, tieneVehiculo, modalVisualizadorMostrado]);

  const handleIrAFirma = () => {
    setMostrarModal(false);
    navigate('/modules/Firma');
  };

  // Mostrar loader mientras verifica
  if (verificando) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#666' }}>Verificando firma...</p>
      </div>
    );
  }

  const handleContinuarVisualizador = () => {
    setMostrarModalVisualizador(false);
  };

  return (
    <>
      {children}

      {/* Modal de modo visualizador (sin vehículo) */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EyeOutlined style={{ color: '#1890ff', fontSize: 24 }} />
            <span>Modo Visualizador</span>
          </div>
        }
        open={mostrarModalVisualizador}
        closable={false}
        maskClosable={false}
        footer={[
          <Button
            key="continuar"
            type="primary"
            size="large"
            icon={<EyeOutlined />}
            onClick={handleContinuarVisualizador}
            style={{ width: '100%', height: 50 }}
          >
            Continuar como Visualizador
          </Button>
        ]}
        width={window.innerWidth > 768 ? 500 : '90%'}
      >
        <Alert
          message="Sin Vehículo Asignado"
          description={
            <>
              <p style={{ marginBottom: 12 }}>
                Actualmente <strong>no tienes un vehículo asignado</strong>.
              </p>
              <p style={{ marginBottom: 12 }}>
                Ingresarás en <strong>modo visualizador</strong>, donde podrás:
              </p>
              <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
                <li>Ver tu historial de traslados realizados</li>
                <li>Consultar información de traslados anteriores</li>
              </ul>
              <p style={{ marginBottom: 0, color: '#ff4d4f' }}>
                ⚠️ No podrás realizar nuevos traslados ni modificar estados
                hasta que se te asigne un vehículo.
              </p>
            </>
          }
          type="info"
          showIcon
        />
      </Modal>

      {/* Modal de firma requerida */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 24 }} />
            <span>Firma Requerida</span>
          </div>
        }
        open={mostrarModal}
        closable={false}
        maskClosable={false}
        footer={[
          <Button
            key="ir-firma"
            type="primary"
            size="large"
            icon={<EditOutlined />}
            onClick={handleIrAFirma}
            style={{ width: '100%', height: 50 }}
          >
            Ir a Registrar Firma
          </Button>
        ]}
        width={window.innerWidth > 768 ? 500 : '90%'}
      >
        <Alert
          message="Firma Obligatoria"
          description={
            <>
              <p style={{ marginBottom: 12 }}>
                Para poder utilizar la aplicación y realizar traslados,
                <strong> debes registrar tu firma digital</strong>.
              </p>
              <p style={{ marginBottom: 12 }}>
                La firma se utilizará para validar los traslados que realices
                y generar los documentos de control de servicio.
              </p>
              <p style={{ marginBottom: 0, color: '#1890ff' }}>
                📝 Por favor, presiona el botón para ir a la sección de firma
                y completar este requisito.
              </p>
            </>
          }
          type="warning"
          showIcon
        />
      </Modal>
    </>
  );
};

// La firma y el modo visualizador son del conductor; el administrador de vehículos no firma servicios
const FirmaGuard = ({ children }) => {
  const { user } = useUser();
  return esAdministrador(user) ? children : <FirmaGuardConductor>{children}</FirmaGuardConductor>;
};

export default FirmaGuard;
